/**
 * API configuration and base client
 * Now uses runtime config instead of static environment variables
 */

import { ConfigManager } from '../config';
import { UserNotFoundError } from './errors';
import { authService } from './auth-service'
/**
 * Gets the current API base URL from configuration
 */
async function getApiBaseUrl(): Promise<string> {
  const configManager = ConfigManager.getInstance();
  const config = await configManager.getConfig();
  if (!config || !config.API_BASE_URL) {
    console.warn('[ApiClient] API_BASE_URL not set in configuration, using fallback.');
  }
    else {
      console.log('[ApiClient] Using API_BASE_URL:', config.API_BASE_URL);
    }
  return config?.API_BASE_URL;
}

export interface ApiConfig {
  baseUrl: string;
  timeout: number;
  headers: Record<string, string>;
}

// Create a function that returns config instead of static config
async function getDefaultConfig(): Promise<ApiConfig> {
  return {
    baseUrl: await getApiBaseUrl(),
    timeout: 10000,
    headers: {
      'Content-Type': 'application/json',
    },
  };
}

export class ApiClient {
  private _config: ApiConfig | null = null;

  constructor(config?: ApiConfig) {
    this._config = config || null;
  }

  // Lazy-load config getter
  private async getConfig(): Promise<ApiConfig> {
    if (!this._config) {
      this._config = await getDefaultConfig();
    }
    return this._config;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {

    const config = await this.getConfig();
    const url = `${config.baseUrl}${endpoint}`;
    const authHeader = await authService.getAuthHeader();

    const response = await fetch(url, {
      ...options,
      headers: {
        ...config.headers,
        ...options.headers,
        ...(authHeader ? { Authorization: authHeader } : {})
      },
    });

    if (!response.ok) {
      if (response.status === 401) {
        let body: any = null;
        try {
          body = await response.json();
        } catch {
          body = null;
        }

        // Emit a global unauthenticated event so the app UI can show login UI.
        // However, if the auth callback is currently processing the authorization
        // code (exchanging /token), suppress the global event so the callback
        // loading UI can finish without being interrupted.
        try {
          const detail = { message: body?.message || body?.error || 'Unauthorized', details: body?.details };
          // If authService reports processing, skip dispatching the global event
          try {
            if ((authService as any).isProcessing && (authService as any).isProcessing()) {
              console.log('[ApiClient] Suppressing tn:unauthenticated because auth callback is in progress');
            } else {
              window.dispatchEvent(new CustomEvent('tn:unauthenticated', { detail }));
            }
          } catch (innerErr) {
            // Fallback: dispatch event if anything goes wrong checking isProcessing
            window.dispatchEvent(new CustomEvent('tn:unauthenticated', { detail }));
          }
        } catch (e) {
          // ignore if window or CustomEvent not available (e.g., tests)
        }

        // If token expired, attempt refresh and retry once
        if (body && body.error === 'TOKEN_EXPIRED') {
          try {
            const refreshed = await authService.refreshIfNeeded(true);
            if (refreshed) {
              const newAuth = await authService.getAuthHeader();
              if (!newAuth) {
                return ({ isAuthenticated: false, error: 'Not authenticated after refresh', statusCode: 401 } as unknown) as T;
              }

              const retryRes = await fetch(url, {
                ...options,
                headers: {
                  ...config.headers,
                  ...options.headers,
                  Authorization: newAuth
                }
              });

              if (!retryRes.ok) {
                let retryBody: any = null;
                try { retryBody = await retryRes.json(); } catch {};
                if (retryRes.status === 401) return ({ isAuthenticated: false, error: retryBody?.message || 'Unauthorized after refresh', statusCode: 401 } as unknown) as T;
                if (retryRes.status === 403) {
                  const rb = retryBody || {};
                  if (rb && rb.error === 'USER_NOT_FOUND') throw new UserNotFoundError();
                  throw new Error(rb?.message || 'Forbidden');
                }
                const errData = await retryRes.json().catch(() => ({}));
                throw new Error(errData.message || `HTTP error! status: ${retryRes.status}`);
              }

              return (await retryRes.json()) as T;
            }
          } catch (err) {
            console.error('[ApiClient] Token refresh failed:', err);
            return ({ isAuthenticated: false, error: 'Session expired or invalid token', statusCode: 401 } as unknown) as T;
          }
        }

        // If token invalid format or other auth errors, return structured unauthenticated response with details
        if (body && body.error) {
          return ({ isAuthenticated: false, error: body.message || body.error, statusCode: 401 } as unknown) as T;
        }

        return ({ isAuthenticated: false, error: body?.message || 'Unauthorized', statusCode: 401 } as unknown) as T;
      }

      if (response.status === 403) {
        const body = await response.json().catch(() => ({}));
        if (body && body.error === 'USER_NOT_FOUND') {
          throw new UserNotFoundError();
        }
        throw new Error(body?.message || 'Forbidden');
      }

      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    return response.json();
  }

  async get<T>(endpoint: string, headers?: Record<string, string>): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'GET',
      headers,
    });
  }

  async post<T>(
    endpoint: string,
    data?: any,
    headers?: Record<string, string>
  ): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
      headers,
    });
  }

  async put<T>(
    endpoint: string,
    data?: any,
    headers?: Record<string, string>
  ): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
      headers,
    });
  }

  async delete<T>(endpoint: string, headers?: Record<string, string>): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'DELETE',
      headers,
    });
  }
}

export const apiClient = new ApiClient();
